---
title: "Czytnik ekranu vs. menu uczelni — co poszło nie tak?"
date: 2026-03-12
slug: nawigacja-niewidoczna-dla-czytnikow-ekranu-case-study-uczelni
description: "Audyt WCAG 2.2 wydziału uczelni publicznej znalazł 5 błędów — w tym menu niewidoczne dla czytników ekranu. Sprawdź co naruszono i jak naprawić to w 14 dni."
tags: [WCAG 2.2, dostępność cyfrowa, aria, czytniki ekranu, uczelnia, prawo]
category: case-study
readingTime: 7 min
---

Student włącza NVDA i wchodzi na stronę wydziału. Szuka sekcji "Studenci". Czytnik milczy — jakby menu w ogóle nie istniało. To nie awaria. To błąd struktury ARIA, jeden z pięciu znalezionych podczas audytu WCAG 2.2 przeprowadzonego w marcu 2026 r. na wydziale polskiej uczelni publicznej.

Każdy z tych błędów był naprawialny. Żaden nie wymagał przepisania strony od zera. Kilka dało się usunąć w godzinę.

---

## Czy uczelnia publiczna musi spełniać WCAG 2.2?

Tak — i nie ma tu pola do interpretacji. Ustawa z 4 kwietnia 2019 r. (Dz.U. poz. 848) nakłada na uczelnie obowiązek zgodności z WCAG 2.2 poziom AA. Ministerstwo Cyfryzacji i MCRPS kontrolują sektor szkolnictwa wyższego priorytetowo — to jeden z obszarów pod szczególnym nadzorem.

Osobna sprawa: deklaracja dostępności. Art. 10 ustawy traktuje jej brak jako autonomiczne naruszenie prawa — niezależnie od tego, jak wygląda reszta strony. Skarga do PFRON, nakaz naprawy, 14–30 dni na działanie. Prawo nie zna wyjątku "bo strona wygląda poprawnie".

{/* TODO: link-internal — link do posta o deklaracji dostępności i Art. 10 */}

---

## Pięć błędów, które czytnik ekranu wykrył w jedną godzinę

### 1. Menu niewidoczne dla czytników ekranu — WCAG 4.1.2

7 razy. Tyle razy struktura nawigacyjna na stronie wydziału była zakodowana tak, że NVDA i VoiceOver nie mogły jej rozpoznać jako menu.

Elementy miały przypisaną rolę `role="menuitem"`, ale brakowało kontenera z `role="menu"`. To fundamentalne naruszenie ARIA. Efekt? Czytnik ekranu odczytuje: "Studenci, link. Pracownicy, link." — bez żadnej informacji, że to menu nawigacyjne, ile ma pozycji i gdzie użytkownik się w nim znajduje.

Błędna struktura (7 wystąpień):

```html
<nav>
  <a href="/studenci" role="menuitem">Studenci</a>
  <a href="/pracownicy" role="menuitem">Pracownicy</a>
  <a href="/rekrutacja" role="menuitem">Rekrutacja</a>
</nav>
```

Poprawna struktura:

```html
<nav aria-label="Nawigacja główna">
  <ul role="menu">
    <li role="none">
      <a href="/studenci" role="menuitem">Studenci</a>
    </li>
    <li role="none">
      <a href="/pracownicy" role="menuitem">Pracownicy</a>
    </li>
    <li role="none">
      <a href="/rekrutacja" role="menuitem">Rekrutacja</a>
    </li>
  </ul>
</nav>
```

Po naprawie czytnik ekranu powie: "Menu nawigacja główna, 3 elementy. Studenci, 1 z 3." To różnica między orientacją a dezorientacją w przestrzeni strony.

---

### 2. Obrazy bez opisu — WCAG 1.1.1

Kryterium 1.1.1 "Treść spoza tekstu" (Poziom A) wymaga opisowej alternatywy tekstowej dla każdego obrazu, który nie jest dekoracją. Dwa obrazy na audytowanej stronie nie miały atrybutu `alt`.

Gdy brakuje `alt`, czytnik ekranu odczyta nazwę pliku. Coś w stylu: "foto_20240912_v2_final.jpg". Albo nie powie nic.

Szybki test: wyłącz wyświetlanie obrazów w przeglądarce. Jeśli strona nadal ma sens — atrybut `alt` jest. Jeśli widzisz puste prostokąty — masz 14 dni na naprawę.

Błąd:
```html
<img src="/zdjecia/budynek-wydzialu.jpg">
```

Poprawnie:
```html
<img src="/zdjecia/budynek-wydzialu.jpg"
     alt="Budynek Wydziału Nauk Ścisłych — wejście główne od strony ul. Akademickiej">
```

Jeśli obraz jest czysto dekoracyjny — `alt=""` z `role="presentation"` nakazuje czytnikowi pominąć element:

```html
<img src="/dekoracja-naglowek.svg" alt="" role="presentation">
```

---

### 3. Strukturalny błąd listy — czytnik mówi nieprawdę

Kryterium 1.3.1 "Informacje i relacje" (Poziom A) wymaga, by struktura wizualna była zakodowana semantycznie. Na stronie wydziału linki były umieszczone bezpośrednio w `<ul>`, z pominięciem `<li>`.

```html
<!-- Błąd: -->
<ul>
  <a href="/harmonogram">Harmonogram zajęć</a>
  <a href="/sesja">Sesja egzaminacyjna</a>
</ul>

<!-- Poprawnie: -->
<ul>
  <li><a href="/harmonogram">Harmonogram zajęć</a></li>
  <li><a href="/sesja">Sesja egzaminacyjna</a></li>
</ul>
```

Nieprawidłowy DOM powoduje, że czytnik ekranu może błędnie podać liczbę elementów listy — albo w ogóle pominąć jej zawartość.

---

### 4. Za małe cele klikalne — WCAG 2.5.5 i 2.5.8

Audyt wykrył 8 elementów interaktywnych mniejszych niż 44×44 piksele. Kryterium 2.5.5 (Poziom AAA) rekomenduje ten rozmiar jako minimalny. Kryterium 2.5.8 w WCAG 2.2 (Poziom AA) wprowadza twardy próg 24×24 px.

Dla osoby z drżeniem rąk albo korzystającej z ekranu dotykowego przycisk 20×20 px to przeszkoda fizyczna — nie estetyczna.

```css
/* Zamiast: */
.btn-icon {
  width: 20px;
  height: 20px;
}

/* Stosuj minimum: */
.btn-icon {
  min-width: 44px;
  min-height: 44px;
  /* Lub powiększ obszar kliknięcia bez zmiany wizualnej: */
  padding: 12px;
}
```

---

### 5. Brak deklaracji dostępności — naruszenie samoistne

Strona wydziału nie publikowała deklaracji dostępności. Art. 10 ustawy z 2019 r. nakłada obowiązek jej opublikowania i aktualizacji co 12 miesięcy — i traktuje brak jako autonomiczne naruszenie prawa, niezależne od stanu technicznego strony.

Wzór deklaracji jest dostępny na gov.pl. Uzupełnienie i opublikowanie zajmuje jeden dzień roboczy.

{/* TODO: link-internal — link do posta lub strony o szablonie deklaracji dostępności */}

---

## Od czego zacząć naprawę — i ile to zajmie

Trzy pierwsze błędy (struktura menu, brak `alt`, błąd listy) to naruszenia Poziomu A — najniższy próg WCAG, który musi być spełniony bezwarunkowo. Od nich zaczynasz.

Szacowany czas naprawy: 2–4 godziny pracy developera frontendowego. Deklarację dostępności opublikujesz równolegle — tego samego dnia. Łączny koszt doprowadzenia strony do zgodności: 3 500–5 500 PLN.

Ryzyko braku działania jest nieproporcjonalnie większe. Każdy może złożyć skargę do PFRON — formularz jest publiczny, a PFRON ma obowiązek każdą rozpatrzyć.

---

## Dlaczego dobre intencje nie zastąpią testu z NVDA

Twórcy strony myśleli o dostępności cyfrowej — to widać. Logiczna hierarchia nagłówków, działający skip link, poprawny atrybut `lang="pl"`, opisowy tytuł strony. Kilka rzeczy zrobionych dobrze.

Tylko że WCAG to system naczyń połączonych. Jeden błąd w strukturze ARIA kasuje korzyści z pozostałych poprawek — czytnik ekranu traci orientację niezależnie od tego, ile innych rzeczy działa. Najczęstsza przyczyna takich sytuacji: brak testowania z rzeczywistymi czytnikami ekranu podczas developmentu.

axe-core wykryje błędy strukturalne. Ale dopiero test z NVDA lub VoiceOver pokaże, co użytkownik niewidomy faktycznie słyszy.

---

## Sprawdź swoją stronę, zanim zrobi to kontrola

Czy Twoja uczelnia jest pewna, że strona wydziału spełnia wymogi ustawy z 2019 r.? Audyt WCAG 2.2 to nie tylko raport z błędami — to mapa konkretnych kroków do zgodności, z priorytetami i szacunkami czasowymi dla zespołu developerskiego.

Skontaktuj się z nami, by umówić bezpłatny przegląd wstępny. W ciągu 48 godzin otrzymasz wstępną ocenę stanu dostępności Twojej strony i listę obszarów wymagających natychmiastowej uwagi.

---

*Dane w artykule pochodzą z anonimowego audytu WCAG 2.2 przeprowadzonego w marcu 2026 r. Nazwa instytucji nie jest ujawniana zgodnie z zasadami poufności.*
